import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Table, Tag, Tooltip, notification } from "antd";
import { useLocation } from "react-router-dom";
import { FAST_API_URL } from "../../../../../api";
import { startSiteLogin } from "../../../../../api/streamEditorAuth";
import { useAuth, useStreamEditorFeature } from "../../../../../providers/AuthProvider";
import {
    acceptedLine,
    ASK_FIX_STATE,
    fixEvents,
    fixLine,
    hhmm,
    langOf,
    OPEN_FIX_STATES,
    STATE_COLOR,
    STATE_MARK,
    STEP_TITLES,
    StepState,
    StreamFix,
    StreamProgressData,
    viewersLine,
} from "./model";
import { releaseFolder } from "./release";
import { StreamDone } from "../StreamDone";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { folderToRelease } from "../../streamLang";

const POLL_MS = 5000;
const LAST_FOLDER_KEY = "stream_editor_last_folder";

const rememberedFolder = (mediaId: string): { folder: number; lang: string } | null => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(LAST_FOLDER_KEY) || "null");
        return saved && saved.media === mediaId ? { folder: Number(saved.folder), lang: String(saved.lang) } : null;
    } catch {
        return null;
    }
};

const rememberFolder = (mediaId: string, folder: number, lang: string) => {
    try {
        sessionStorage.setItem(LAST_FOLDER_KEY, JSON.stringify({ media: mediaId, folder, lang }));
    } catch {
        /* storage off — release works within this page only */
    }
};
const STEPS = ["ru", "translate", "voice", "air"];

/** Подсказка блока статусов: на узком окне его хвост обрезан — целиком он здесь. */
export const statusTitle = (viewers: string | null | undefined, openFixes: number, error: string | null): string =>
    [
        "Подробности: все папки эфира и правки",
        openFixes > 0 ? `правок в работе: ${openFixes}` : "",
        error ? `прогресс не получен: ${error}` : "",
        viewers || "",
    ]
        .filter(Boolean)
        .join(" · ");

interface Props {
    mediaId: string;
    folder: number;
    lang: string;
    isRealtimeStream: boolean;
}

/**
 * Полоса прогресса папки эфира и тост-отчёт правки (W4.4, фаза 4) — только переводчику эфира из списка
 * (вход через сайт, `features: stream_progress`). Остальным — ссылка «Войти через сайт» на эфире, и всё.
 *
 * Кратко: «русский ✓ — перевод ⟳ — голос · — в эфире · — зрители увидят в ЧЧ:ММ»; по клику — все папки и правки.
 * Правка переводчика уходит в эфир сама (полоса правок site_server): тост говорит «принята», потом итог —
 * «в эфире ЧЧ:ММ, зрители увидят в ЧЧ:ММ» или почему не вышло. Уход с папки — её правки в эфир сразу (release).
 */
export const StreamProgress: React.FC<Props> = ({ mediaId, folder, lang, isRealtimeStream }) => {
    const { user } = useAuth();
    const location = useLocation();
    const enabled = useStreamEditorFeature("stream_progress");
    const [data, setData] = useState<StreamProgressData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const seen = useRef<Map<string, string>>(new Map());
    const firstPass = useRef(true);
    const lastFolder = useRef<{ folder: number; lang: string } | null>(null);
    const [api, holder] = notification.useNotification();
    const originalLang = (useAudioStateContext() as { originalLang?: string } | undefined)?.originalLang;

    const refresh = useCallback(async () => {
        if (!enabled || !mediaId) return;
        try {
            const response = await fetch(`${FAST_API_URL}/progress?media=${encodeURIComponent(mediaId)}`);
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                setError(body?.detail?.message || body?.detail || `прогресс не получен (${response.status})`);
                return;
            }
            setError(null);
            setData(body as StreamProgressData);
            const fixes: StreamFix[] = [];
            for (const f of (body as StreamProgressData).folders || []) {
                for (const l of Object.values(f.langs || {})) fixes.push(...(l.fixes || []));
            }
            const events = fixEvents(fixes, seen.current, user?.email || null, firstPass.current);
            firstPass.current = false;
            for (const ev of events) {
                const where = `папка ${ev.fix.folder}`;
                if (ev.kind === "accepted") {
                    api.info({
                        message: `Правка принята (${where})`,
                        description: acceptedLine(ev.fix),
                        placement: "bottomRight",
                        duration: 4,
                    });
                } else if (ev.fix.state === "fixed") {
                    api.success({ message: `Правка в эфире (${where})`, description: fixLine(ev.fix),
                        placement: "bottomRight", duration: 15 });
                } else if (ev.fix.state === "nothing_to_fix") {
                    api.info({ message: `Правка (${where})`, description: fixLine(ev.fix), placement: "bottomRight" });
                } else if (ev.fix.state === ASK_FIX_STATE) {
                    // П.50г: правка не влезла и временем — не сбой, а вопрос переводчику; соседние реплики не тронуты.
                    api.warning({
                        message: `Правка не влезает (${where}) — нужен ваш ответ`,
                        description: `${fixLine(ev.fix)}. Сократите свой текст здесь или в плеере («Мои правки») ` +
                            "разрешите сократить соседнюю реплику.",
                        placement: "bottomRight",
                        duration: 0,
                    });
                } else {
                    api.error({ message: `Правка не дошла до эфира (${where})`, description: fixLine(ev.fix),
                        placement: "bottomRight", duration: 0 });
                }
            }
        } catch (err) {
            setError((err as Error).message);
        }
    }, [enabled, mediaId, api, user?.email]);

    useEffect(() => {
        if (!enabled || !isRealtimeStream) return undefined;
        void refresh();
        const timer = window.setInterval(refresh, POLL_MS);
        return () => window.clearInterval(timer);
    }, [enabled, isRealtimeStream, refresh]);

    // Переводчик ушёл с папки — её ждущие правки уходят в эфир сразу (CONTRACTS п.15). Смена папки в редакторе
    // перезагружает страницу, поэтому прежняя папка помнится и в sessionStorage этой вкладки.
    useEffect(() => {
        if (!enabled || !isRealtimeStream || !folder) return;
        const prev = lastFolder.current || rememberedFolder(mediaId);
        lastFolder.current = { folder, lang };
        rememberFolder(mediaId, folder, lang);
        // Только папку языка перевода: русский столбец заявок правок не держит, `release` с `lang: "ru"` — 422
        // (проба P50m10c: заход на ?folder=1&lang=en после ?folder=1 без языка).
        const gone = folderToRelease(prev, { folder, lang }, originalLang);
        if (gone) {
            void releaseFolder(mediaId, gone.folder, gone.lang).catch(() => undefined);
        }
    }, [enabled, isRealtimeStream, folder, lang, mediaId, originalLang]);

    const current = useMemo(() => {
        const f = data?.folders?.find((item) => item.folder === Number(folder));
        return { folder: f, lang: langOf(f, lang) };
    }, [data, folder, lang]);

    if (!isRealtimeStream) return null;
    // «Готово — в эфир» и «← К эфиру» — рядом с полосой; кнопка сама знает, видна ли она (stream_fixes).
    const done = (
        <StreamDone
            mediaId={mediaId}
            folder={folder}
            lang={lang}
            isRealtimeStream={isRealtimeStream}
            data={enabled ? data : null}
            onReleased={() => void refresh()}
        />
    );
    if (!enabled) {
        if (user?.site) return done;
        return (
            <>
                {done}
                <Tooltip title="Переводчику эфира из списка: правки сами уходят в эфир, видно, как идут папки">
                    <a
                        style={{ fontSize: 11, marginRight: 8 }}
                        onClick={() => startSiteLogin(`${window.location.origin}${location.pathname}${location.search}`)}
                    >
                        Войти через сайт
                    </a>
                </Tooltip>
            </>
        );
    }

    const steps = current.lang?.steps;
    const viewers = viewersLine(current.folder?.public_at);
    const openFixes = current.lang?.fixes_open || 0;

    const stepTag = (id: string, state?: StepState) => (
        <Tag key={id} color={STATE_COLOR[state || "pending"]} style={{ marginInlineEnd: 2, fontSize: 11 }}>
            {STEP_TITLES[id]} {STATE_MARK[state || "pending"]}
        </Tag>
    );

    const folderRows = (data?.folders || []).map((f) => {
        const l = langOf(f, lang);
        return {
            key: f.folder,
            folder: f.folder,
            steps: l?.steps,
            published: hhmm(l?.published_at),
            viewers: viewersLine(f.public_at),
            title: l?.steps?.title || l?.stage,
            fixes: l?.fixes || [],
        };
    });

    return (
        <>
            {holder}
            {/* Блок статусов шапки (`x_head_status`): одна строка, в шапке сжимается и обрезается внутри себя, —
                появление счётчика или статуса не переносит шапку на вторую строку и не сдвигает ленту (P50m10, E6).
                «Правок в работе» — первым: при 1280 px обрезка не прячет ход своей правки; «зрители видят с…» —
                последним: на узком окне обрезается первым; всё целиком — в подсказке. */}
            <span
                data-testid="stream-progress"
                className="x_head_status"
                style={{ fontSize: 11, marginRight: 8, cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={() => setOpen(true)}
                title={statusTitle(viewers, openFixes, error)}
            >
                {openFixes > 0 && (
                    <Tag
                        data-testid="stream-progress-open-fixes"
                        color="processing"
                        style={{ marginInlineEnd: 4, fontSize: 11 }}
                    >
                        правок в работе: {openFixes}
                    </Tag>
                )}
                {steps ? STEPS.map((id) => stepTag(id, steps[id] as StepState)) : <Tag>прогресс…</Tag>}
                {error && (
                    <Tooltip title={error}>
                        <Tag color="error" style={{ marginLeft: 4, fontSize: 11 }}>
                            прогресс?
                        </Tag>
                    </Tooltip>
                )}
                {viewers && <span style={{ marginLeft: 4 }}>{viewers}</span>}
            </span>
            {done}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={980}
                title={`Эфир: папки и правки${data?.viewer_delay_sec ? ` · задержка зрителей ${Math.round(data.viewer_delay_sec / 60)} мин` : ""}`}
            >
                <Table
                    size="small"
                    pagination={false}
                    dataSource={folderRows}
                    rowClassName={(row) => (row.folder === Number(folder) ? "ant-table-row-selected" : "")}
                    columns={[
                        { title: "Папка", dataIndex: "folder", width: 64 },
                        {
                            title: "Шаги",
                            dataIndex: "steps",
                            render: (s) => (s ? STEPS.map((id) => stepTag(id, s[id])) : "—"),
                        },
                        { title: "Сейчас", dataIndex: "title", width: 180 },
                        { title: "В эфире", dataIndex: "published", width: 80 },
                        { title: "Зрители", dataIndex: "viewers", width: 160 },
                        {
                            title: "Правки",
                            dataIndex: "fixes",
                            render: (fixes: StreamFix[]) =>
                                fixes.length ? (
                                    <div>
                                        {fixes.slice(0, 5).map((fx) => (
                                            <div key={fx.id} style={{ fontSize: 11 }}>
                                                <Tag
                                                    color={
                                                        fx.state === "fixed"
                                                            ? "green"
                                                            : OPEN_FIX_STATES.includes(fx.state)
                                                              ? "processing"
                                                              : fx.state === "nothing_to_fix"
                                                                ? "default"
                                                                : "error"
                                                    }
                                                >
                                                    {fx.state}
                                                </Tag>
                                                {fixLine(fx)}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    "—"
                                ),
                        },
                    ]}
                />
                {data?.fixes_error && <div style={{ color: "#cf1322", marginTop: 8 }}>{data.fixes_error}</div>}
            </Modal>
        </>
    );
};
